import { axios } from "../axiosInstance";
import { IOfficeNew } from "../interfaces/models/IOffice";
import {
  buildJurisdictionGuidCatalog,
  collectDistinctJurisdictionOptions,
  type FirmJurisdictionSource,
} from "../utils/resolveFirmJurisdictionDisplay";
import {
  isJurisdiction,
  jurisdictionContains,
} from "../utils/jurisdiction/structuredJurisdiction";

export const getOffice = async () => {
  try {
    const offices = await axios.Get("./office");
    const data = offices.data;
    return data;
  } catch (error) {
    console.error("Error fetching data:", error);
    return [];
  }
};

export const getOfficeNewByFirm = async (
  id: string,
  showInDirectory?: boolean,
): Promise<IOfficeNew[]> => {
  try {
    const filter: Record<
      string,
      { value: string | boolean; comparisonType: string }
    > = {
      firm: { value: id, comparisonType: "EQUAL" },
    };
    if (showInDirectory !== undefined) {
      filter.showInDirectory = {
        value: showInDirectory,
        comparisonType: "EQUAL",
      };
    }
    const params = new URLSearchParams();
    params.append("filter", JSON.stringify(filter));
    const offices = await axios.Get(`./office?${params.toString()}`);
    return offices.data;
  } catch (error) {
    console.error("Error fetching data:", error);
    return [];
  }
};

export const getOfficeByFirm = async (
  id: string,
  showInDirectory?: boolean,
): Promise<IOfficeNew[]> => {
  try {
    const filter: Record<
      string,
      { value: string | boolean; comparisonType: string }
    > = {
      firm: { value: id, comparisonType: "EQUAL" },
    };
    if (showInDirectory !== undefined) {
      filter.showInDirectory = {
        value: showInDirectory,
        comparisonType: "EQUAL",
      };
    }
    const params = new URLSearchParams();
    params.append("filter", JSON.stringify(filter));
    const offices = await axios.Get(`./office?${params.toString()}`);
    return offices.data;
  } catch (error) {
    console.error("Error fetching data:", error);
    return [];
  }
};

export const getOfficeById = async (
  id: string,
): Promise<IOfficeNew | undefined> => {
  try {
    const params = new URLSearchParams();
    params.append(
      "filter",
      JSON.stringify({
        id: { value: id, comparisonType: "EQUAL" },
      }),
    );

    const response = await axios.Get(`./office?${params.toString()}`);
    return response.data?.[0];
  } catch (error) {
    console.error("Error fetching office by id:", error);
    return undefined;
  }
};

export const getOfficeByLegacyId = async (
  legacyId: string,
): Promise<IOfficeNew | undefined> => {
  try {
    const params = new URLSearchParams();
    params.append(
      "filter",
      JSON.stringify({
        legacyId: { value: legacyId, comparisonType: "EQUAL" },
      }),
    );

    const response = await axios.Get(`./office?${params.toString()}`);
    return response.data?.[0];
  } catch (error) {
    console.error("Error fetching office by legacy id:", error);
    return undefined;
  }
};

export const getOfficeByIdOrLegacyId = async (
  id: string,
): Promise<IOfficeNew | undefined> => {
  const trimmedId = id.trim();
  const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    trimmedId,
  );

  return isUuid ? getOfficeById(trimmedId) : getOfficeByLegacyId(trimmedId);
};

export const updateOffice = async (office: IOfficeNew) => {
  try {
    const response = await axios.Put(`/office`, office);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const createContent = async (office: IOfficeNew) => {
  try {
    const response = await axios.Post(`/office`, `${office}`);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const createOffice = async (
  office: Omit<IOfficeNew, "id">,
): Promise<IOfficeNew> => {
  try {
    const response = await axios.Post("/office", office);
    if (response.data?.error) {
      throw new Error(
        Array.isArray(response.data.error)
          ? response.data.error.join(", ")
          : String(response.data.error),
      );
    }
    return response.data;
  } catch (error) {
    console.error("Error creating office:", error);
    throw error;
  }
};

async function fetchJurisdictionSources(): Promise<FirmJurisdictionSource[]> {
  const officeParams = new URLSearchParams();
  officeParams.append(
    "fields",
    JSON.stringify(["jurisdiction", "jurisdictionName"]),
  );

  const officeResponse = await axios.Get(`./office?${officeParams.toString()}`);
  const offices = Array.isArray(officeResponse.data) ? officeResponse.data : [];

  return offices;
}

/**
 * Distinct jurisdiction options ({ id, name }) derived from offices.
 * Uses resolveFirmJurisdictionDisplay so offices with only a readable
 * `jurisdiction` or `jurisdictionName` still appear in filter dropdowns.
 */
export const getOfficeJurisdictionOptions = async (): Promise<
  { id: string; name: string }[]
> => {
  try {
    const sources = await fetchJurisdictionSources();
    return collectDistinctJurisdictionOptions(sources);
  } catch (error) {
    console.error("Error fetching office jurisdictions:", error);
    return [];
  }
};

export const getOfficeJurisdictionFilterData = async (): Promise<{
  options: { id: string; name: string }[];
  catalog: { id: string; name: string }[];
}> => {
  const sources = await fetchJurisdictionSources();
  return {
    options: collectDistinctJurisdictionOptions(sources),
    catalog: buildJurisdictionGuidCatalog(sources),
  };
};

/** GUID -> label catalog for legacy jurisdiction ids stored on registrations. */
export const getOfficeJurisdictionCatalog = async (): Promise<
  { id: string; name: string }[]
> => {
  try {
    const sources = await fetchJurisdictionSources();
    return buildJurisdictionGuidCatalog(sources);
  } catch (error) {
    console.error("Error fetching office jurisdiction catalog:", error);
    return [];
  }
};

/** Offices inside a firm jurisdiction: same country, then contained by level. */
export const getOfficeByJurisdiction = async (
  jurisdiction: unknown,
): Promise<{ id: string }[]> => {
  const params = new URLSearchParams();
  if (!isJurisdiction(jurisdiction)) {
    params.append("fields", JSON.stringify(["id"]));
    params.append(
      "filter",
      JSON.stringify({
        jurisdiction: { value: String(jurisdiction ?? ""), comparisonType: "EQUAL" },
      }),
    );
    const offices = await axios.Get(`./office?${params.toString()}`);
    return offices.data;
  }

  params.append("fields", JSON.stringify(["id", "jurisdiction"]));
  params.append(
    "filter",
    JSON.stringify({
      "jurisdiction.countryCode": {
        value: jurisdiction.countryCode,
        comparisonType: "EQUAL",
      },
    }),
  );
  const response = await axios.Get(`./office?${params.toString()}`);
  const offices: { id: string; jurisdiction?: unknown }[] = Array.isArray(
    response.data,
  )
    ? response.data
    : [];
  return offices
    .filter((office) => jurisdictionContains(jurisdiction, office.jurisdiction))
    .map((office) => ({ id: office.id }));
};

export const getOfficeByJurisdictionGroup = async (jurisdictionId: string) => {
  try {
    const params = new URLSearchParams();
    params.append("fields", JSON.stringify(["id"]));
    params.append(
      "filter",
      JSON.stringify({
        jurisdictionGroup: { value: jurisdictionId, comparisonType: "EQUAL" },
      }),
    );

    const offices = await axios.Get(`./office?${params.toString()}`);
    return offices.data;
  } catch (error) {
    throw error;
  }
};

export const getLocationsByFirm = async (id: string): Promise<IOfficeNew[]> => {
  try {
    const params = new URLSearchParams();
    params.append(
      "filter",
      JSON.stringify({
        firm: { value: id, comparisonType: "EQUAL" },
        showInDirectory: { value: true, comparisonType: "EQUAL" },
      }),
    );

    const offices = await axios.Get(`./office?${params.toString()}`);
    const data = offices.data;
    return data;
  } catch (error) {
    console.error("Error fetching data:", error);
    return [];
  }
};
